import api from './api';

// Auth service for handling authentication operations
class AuthService {
  constructor() {
    this.token = null;
    this.userData = null;
    this.sessionTimeoutId = null;
    this.sessionCheckInterval = null;
    this.init();
  }

  // Initialize service with stored data
  init() {
    this.token = localStorage.getItem('authToken');
    this.userData = this.getUserData();
    
    // Start session timeout monitoring if user is authenticated
    if (this.isAuthenticated() && !this.isTokenExpired()) {
      this.startSessionTimeout();
    }
  }

  // Login method
  async login(credentials) {
    try {
      // Clear any old session data before login
      console.log('AuthService: Clearing old session data before new login');
      this.stopSessionTimeout();
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('username');
      localStorage.removeItem('orgId');
      localStorage.removeItem('appId');
      localStorage.removeItem('labTypeId');
      localStorage.removeItem('userTypeId');
      localStorage.removeItem('isSubUser');
      localStorage.removeItem('mainOrgId');
      localStorage.removeItem('mainAppId');
      localStorage.removeItem('subUserOrgId');
      localStorage.removeItem('disciplineId');
      localStorage.removeItem('disciplineIds');
      
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.success) {
        const { 
          username, 
          orgId, 
          appId,
          labTypeId, 
          userTypeId,
          token, 
          redirectTo,
          passwordChangeRequired,
          isSubUser,
          mainOrgId 
        } = response.data.data;

        // Store authentication token
        if (token) {
          this.token = token;
          localStorage.setItem('authToken', token);
        }

        // Store user data in localStorage with NEW login time
        const userData = {
          username,
          orgId, // This is your orgid
          appId: appId || orgId, // This is your appid
          labTypeId, // This is your labtypeid
          userTypeId, // This is your usertypeid
          isSubUser: isSubUser || false,
          mainOrgId: mainOrgId || null,
          loginTime: new Date().toISOString(), // Fresh login time
          redirectTo: redirectTo || '/dashboard'
        };

        this.userData = userData;
        localStorage.setItem('userData', JSON.stringify(userData));

        // Store individual items for easy access
        localStorage.setItem('username', username);
        localStorage.setItem('orgId', orgId.toString());
        localStorage.setItem('appId', (appId || orgId).toString());
        localStorage.setItem('labTypeId', labTypeId ? labTypeId.toString() : '');
        localStorage.setItem('userTypeId', userTypeId ? userTypeId.toString() : ''); // Store UserTypeId
        
        console.log('AuthService: New login successful, session started at:', userData.loginTime);
        
        // Start fresh session timeout monitoring
        this.startSessionTimeout();
        
        return {
          success: true,
          data: userData,
          passwordChangeRequired,
          redirectTo
        };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }
  // Login method (auto-login)
  async loginAuto(credentials) {
    try {
      // Clear any old session data before login
      console.log('AuthService: Clearing old session data before auto-login');
      this.stopSessionTimeout();
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('username');
      localStorage.removeItem('orgId');
      localStorage.removeItem('appId');
      localStorage.removeItem('labTypeId');
      localStorage.removeItem('userTypeId');
      localStorage.removeItem('isSubUser');
      localStorage.removeItem('mainOrgId');
      localStorage.removeItem('mainAppId');
      localStorage.removeItem('subUserOrgId');
      localStorage.removeItem('disciplineId');
      localStorage.removeItem('disciplineIds');
      
      const response = await api.post('/auth/login-auto', credentials);

      if (response.data.success) {
        const { 
          username, 
          orgId, 
          appId,
          labTypeId, 
          userTypeId,
          token, 
          redirectTo,
          passwordChangeRequired,
          isSubUser,
          mainOrgId 
        } = response.data.data;

        // Store authentication token
        if (token) {
          this.token = token;
          localStorage.setItem('authToken', token);
        }

        // Store user data in localStorage with NEW login time
        const userData = {
          username,
          orgId, // This is your orgid
          appId: appId || orgId, // This is your appid
          labTypeId, // This is your labtypeid
          userTypeId, // This is your usertypeid
          isSubUser: isSubUser || false,
          mainOrgId: mainOrgId || null,
          loginTime: new Date().toISOString(), // Fresh login time
          redirectTo: redirectTo || '/dashboard'
        };

        this.userData = userData;
        localStorage.setItem('userData', JSON.stringify(userData));

        // Store individual items for easy access
        localStorage.setItem('username', username);
        localStorage.setItem('orgId', orgId.toString());
        localStorage.setItem('appId', (appId || orgId).toString());
        localStorage.setItem('labTypeId', labTypeId ? labTypeId.toString() : '');
        localStorage.setItem('userTypeId', userTypeId ? userTypeId.toString() : ''); // Store UserTypeId
        
        console.log('AuthService: Auto-login successful, session started at:', userData.loginTime);
        
        // Start fresh session timeout monitoring
        this.startSessionTimeout();
        
        return {
          success: true,
          data: userData,
          passwordChangeRequired,
          redirectTo
        };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout method
  logout(shouldRedirect = true) {
    console.log('AuthService: Logging out user');
    
    // Stop session timeout monitoring
    this.stopSessionTimeout();
    
    this.token = null;
    this.userData = null;
    
    // Clear all auth-related data from localStorage - including sub-user data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('username');
    localStorage.removeItem('orgId');
    localStorage.removeItem('appId');
    localStorage.removeItem('labTypeId');
    localStorage.removeItem('userTypeId');
    localStorage.removeItem('isSubUser');
    localStorage.removeItem('mainOrgId');
    localStorage.removeItem('mainAppId');
    localStorage.removeItem('subUserOrgId');
    localStorage.removeItem('disciplineId');
    localStorage.removeItem('disciplineIds');
    
    console.log('AuthService: All session data cleared');
    
    // Only redirect if explicitly requested
    if (shouldRedirect) {
      // Use setTimeout to avoid immediate redirect during initialization
      setTimeout(() => {
        window.location.href = '/main/login';
      }, 100);
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!this.userData;
  }

  // Get authentication token
  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  // Get user data
  getUserData() {
    if (this.userData) return this.userData;
    
    const storedData = localStorage.getItem('userData');
    if (storedData) {
      try {
        return JSON.parse(storedData);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        return null;
      }
    }
    return null;
  }

  // Get specific user info
  getUsername() {
    const userData = this.getUserData();
    return userData?.username || localStorage.getItem('username');
  }

  getOrgId() {
    const userData = this.getUserData();
    return userData?.orgId || localStorage.getItem('orgId');
  }

  getAppId() {
    const userData = this.getUserData();
    return userData?.appId || localStorage.getItem('appId');
  }

  getLabTypeId() {
    const userData = this.getUserData();
    return userData?.labTypeId || localStorage.getItem('labTypeId');
  }

  getUserTypeId() {
    const userData = this.getUserData();
    return userData?.userTypeId || localStorage.getItem('userTypeId');
  }

  isSubUser() {
    const userData = this.getUserData();
    return userData?.isSubUser || false;
  }

  getMainOrgId() {
    const userData = this.getUserData();
    return userData?.mainOrgId;
  }

  // Change password method
  async changePassword(passwordData) {
    try {
      const response = await api.post('/auth/change-password', passwordData);
      
      if (response.data.success) {
        const { 
          username, 
          orgId, 
          appId,
          labTypeId, 
          userTypeId,
          token, 
          redirectTo,
          isSubUser 
        } = response.data.data;

        // Update stored data after password change
        if (token) {
          this.token = token;
          localStorage.setItem('authToken', token);
        }

        const userData = {
          username,
          orgId,
          appId: appId || orgId,
          labTypeId,
          userTypeId,
          isSubUser: isSubUser || false,
          loginTime: new Date().toISOString(),
          redirectTo: redirectTo || '/dashboard'
        };

        this.userData = userData;
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('username', username);
        localStorage.setItem('orgId', orgId.toString());
        localStorage.setItem('appId', (appId || orgId).toString());
        localStorage.setItem('labTypeId', labTypeId ? labTypeId.toString() : '');
        localStorage.setItem('userTypeId', userTypeId ? userTypeId.toString() : ''); // Store UserTypeId

        return {
          success: true,
          data: userData,
          redirectTo
        };
      } else {
        throw new Error(response.data.message || 'Password change failed');
      }
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  // Register laboratory method
  async registerLaboratory(labData) {
    try {
      const response = await api.post('/auth/register', labData);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Send OTP method
  async sendOTP(data) {
    try {
      const response = await api.post('/auth/send-otp', data);
      return response.data;
    } catch (error) {
      console.error('Send OTP error:', error);
      throw error;
    }
  }

  // Verify OTP method
  async verifyOTP(data) {
    try {
      const response = await api.post('/auth/verify-otp', data);
      return response.data;
    } catch (error) {
      console.error('Verify OTP error:', error);
      throw error;
    }
  }

  // Refresh token if needed (implement based on your backend)
  async refreshToken() {
    // For now, return null as refresh is not implemented
    // TODO: Implement token refresh logic when backend supports it
    return null;
  }

  // Check if token is expired (basic check - implement proper JWT validation if needed)
  isTokenExpired() {
    if (!this.token) {
      console.log('AuthService: No token found');
      return true;
    }
    
    try {
      const userData = this.getUserData();
      if (!userData?.loginTime) {
        console.log('AuthService: No login time found, considering token valid');
        return false;
      }
      
      const loginTime = new Date(userData.loginTime);
      const now = new Date();
      const hoursSinceLogin = (now - loginTime) / (1000 * 60 * 60);
      
      console.log('AuthService: Token expiration check', {
        loginTime: loginTime.toISOString(),
        hoursSinceLogin: hoursSinceLogin.toFixed(2),
        isExpired: hoursSinceLogin > 3
      });
      
      // Consider token expired after 3 hours for automatic logout
      return hoursSinceLogin > 3;
    } catch (error) {
      console.error('AuthService: Error checking token expiration:', error);
      return true;
    }
  }

  // Start automatic session timeout monitoring
  startSessionTimeout() {
    // Clear any existing timeout
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }

    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    if (!this.isAuthenticated()) {
      return;
    }

    const userData = this.getUserData();
    if (!userData?.loginTime) {
      return;
    }

    const loginTime = new Date(userData.loginTime);
    const now = new Date();
    const timeElapsed = now - loginTime;
    const threeHoursInMs = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
    const timeRemaining = threeHoursInMs - timeElapsed;

    console.log('AuthService: Starting session timeout monitoring', {
      loginTime: loginTime.toISOString(),
      timeElapsed: Math.round(timeElapsed / 1000 / 60) + ' minutes',
      timeRemaining: Math.round(timeRemaining / 1000 / 60) + ' minutes'
    });

    if (timeRemaining <= 0) {
      // Session already expired
      console.log('AuthService: Session already expired, logging out immediately');
      this.handleSessionExpiry();
      return;
    }

    // Set timeout for session expiry
    this.sessionTimeoutId = setTimeout(() => {
      console.log('AuthService: Session timeout reached, logging out user');
      this.handleSessionExpiry();
    }, timeRemaining);

    // Also set up interval to check session status every minute
    this.sessionCheckInterval = setInterval(() => {
      if (this.isTokenExpired()) {
        console.log('AuthService: Session expired during periodic check, logging out user');
        this.handleSessionExpiry();
      }
    }, 60000); // Check every minute
  }

  // Stop session timeout monitoring
  stopSessionTimeout() {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }

    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    console.log('AuthService: Session timeout monitoring stopped');
  }

  // Extend session by 30 minutes
  extendSession() {
    const userData = this.getUserData();
    if (!userData) {
      console.log('AuthService: Cannot extend session - no user data found');
      return false;
    }

    // Add 30 minutes to the current login time
    const currentLoginTime = new Date(userData.loginTime);
    const extendedTime = new Date(currentLoginTime.getTime() + (30 * 60 * 1000)); // Add 30 minutes

    console.log('AuthService: Extending session', {
      currentLoginTime: currentLoginTime.toISOString(),
      extendedTime: extendedTime.toISOString(),
      extensionMinutes: 30
    });

    // Update the login time in userData
    const updatedUserData = {
      ...userData,
      loginTime: extendedTime.toISOString()
    };

    this.userData = updatedUserData;
    localStorage.setItem('userData', JSON.stringify(updatedUserData));

    // Restart session timeout monitoring with new time
    this.startSessionTimeout();

    console.log('AuthService: Session extended successfully by 30 minutes');
    return true;
  }

  // Handle session expiry
  handleSessionExpiry() {
    console.log('AuthService: Handling session expiry');
    
    // Stop any running timeouts
    this.stopSessionTimeout();
    
    // Show notification to user
    if (typeof window !== 'undefined' && window.alert) {
      alert('Your session has expired. You will be redirected to the login page.');
    }
    
    // Log out user
    this.logout(true);
  }

  // Verify user password for payment authorization
  async verifyPassword(orgId, password) {
    try {
      const response = await api.post('/auth/verify-password', {
        orgId: orgId,
        password: password
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying password:', error);
      throw error;
    }
  }

  // Update user profile data
  updateUserData(newData) {
    if (this.userData) {
      this.userData = { ...this.userData, ...newData };
      localStorage.setItem('userData', JSON.stringify(this.userData));
      
      // Update individual items if they're included
      if (newData.username) localStorage.setItem('username', newData.username);
      if (newData.orgId) localStorage.setItem('orgId', newData.orgId.toString());
      if (newData.appId) localStorage.setItem('appId', newData.appId.toString());
      if (newData.labTypeId) localStorage.setItem('labTypeId', newData.labTypeId.toString());
      if (newData.userTypeId) localStorage.setItem('userTypeId', newData.userTypeId.toString()); // Update UserTypeId
    }
  }

  // Get redirect URL based on user type
  getRedirectUrl() {
    const userData = this.getUserData();
    if (userData?.redirectTo) {
      return userData.redirectTo;
    }
    
    const userTypeId = this.getUserTypeId();
    
    if (!userTypeId || userTypeId === '' || userTypeId === '0') {
      return '/corruption-declaration';
    }
    
    switch (userTypeId) {
      case '1':
      case '2':
      case '24':
        return '/admin/dashboard';
      case '3':
        return '/rr/dashboard';
      case '4':
        return '/do/dashboard';
      case '5':
        return '/accounts/dashboard';
      case '6':
        return '/assessor/dashboard';
      case '8':
        return '/director/dashboard';
      case '9':
        return '/jd/dashboard';
      case '10':
        return '/ceo/dashboard';
      case '11':
        return '/convener/dashboard';
      default:
        return '/dashboard';
    }
  }

  // Handle session expiry
  handleSessionExpiry() {
    console.log('AuthService: Handling session expiry');
    
    // Stop any running timeouts
    this.stopSessionTimeout();
    
    // Show notification to user using toast if available
    if (typeof window !== 'undefined') {
      // Try to use toast if available, otherwise fallback to alert
      if (window.toast && typeof window.toast.warning === 'function') {
        window.toast.warning('Your session has expired after 3 hours. You will be redirected to the login page.');
      } else if (window.alert) {
        alert('Your session has expired after 3 hours. You will be redirected to the login page.');
      }
    }
    
    // Log out user
    this.logout(true);
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;